import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { drivePrepStatus, drivePrepPurchase, drivePrepStart, drivePrepSubmit } from "@/lib/campusApi";
import { completePurchase } from "@/lib/razorpay";
import {
  Loader2, ArrowLeft, Sparkles, CheckCircle2, XCircle, RotateCcw, Lock, FileText, Clock,
} from "lucide-react";

/* Drive Prep — paid past-pattern practice for an upcoming drive.
   3 fresh papers in the company's real test pattern, instant self-grading,
   no proctoring (this is practice — reps are the point). */

export default function DrivePrepPage() {
  const { driveId } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [stage, setStage] = useState("loading"); // loading | intro | live | result
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = () => drivePrepStatus(driveId)
    .then(({ data }) => { setMeta(data); setStage("intro"); })
    .catch((e) => { setErr(e?.response?.data?.detail || "Could not load."); setStage("intro"); });
  useEffect(() => { load(); }, [driveId]); // eslint-disable-line

  const buy = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await drivePrepPurchase(driveId);
      await completePurchase(data); // Razorpay when live, instant in mock mode
      load();
    }
    catch (e) { setErr(e?.message || e?.response?.data?.detail || "Purchase failed."); }
    finally { setBusy(false); }
  };

  const start = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await drivePrepStart(driveId);
      setTest(data); setAnswers({}); setStage("live");
    } catch (e) { setErr(e?.response?.data?.detail || "Could not start."); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await drivePrepSubmit(driveId, test.test_id, answers);
      setResult(data.result); setStage("result");
    } catch (e) { setErr(e?.response?.data?.detail || "Submit failed."); }
    finally { setBusy(false); }
  };

  if (stage === "loading") return <div className="container-x py-16 text-stone-500 flex items-center gap-2"><Loader2 size={18} className="animate-spin" /> Loading…</div>;

  return (
    <div className="container-x py-10 md:py-14" data-testid="drive-prep-page">
      <button onClick={() => navigate("/app/campus")} className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-900 mb-4"><ArrowLeft size={14} /> Back to drives</button>

      {stage === "intro" && meta && (
        <div className="max-w-2xl">
          <span className="overline">Drive prep · {meta.pattern_name}</span>
          <h1 className="font-display text-3xl md:text-4xl mt-2">{meta.drive.company_name} — practise before the real test.</h1>
          <p className="text-sm text-stone-600 mt-3">Fresh papers in this company's known test pattern, new questions every attempt, instant results with the correct answers. Practice here is unproctored — reps are the point.</p>

          {err && <p className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-2">{err}</p>}

          {!meta.purchased ? (
            <div className="mt-6 rounded-3xl bg-stone-900 text-stone-50 p-7" data-testid="prep-paywall">
              <span className="inline-flex p-2.5 rounded-xl bg-amber-300 text-stone-900"><Lock size={18} /></span>
              <h2 className="font-display text-2xl mt-3">Drive Prep — ₹{meta.price_inr}</h2>
              <ul className="mt-3 space-y-1.5 text-sm text-stone-300">
                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-amber-300" /> {meta.papers_per_pack} full practice papers in the {meta.pattern_name} structure</li>
                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-amber-300" /> Fresh questions every paper — can't be memorised</li>
                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-amber-300" /> Instant scoring with correct answers shown</li>
              </ul>
              <button onClick={buy} disabled={busy} data-testid="prep-buy"
                className="mt-5 px-6 py-3 rounded-full bg-amber-300 text-stone-900 font-semibold hover:bg-amber-200 disabled:opacity-60">
                {busy ? <Loader2 size={15} className="animate-spin inline" /> : `Unlock for ₹${meta.price_inr}`}
              </button>
              <p className="mt-2 text-[11px] text-stone-400">Test mode — no real charge yet.</p>
            </div>
          ) : (
            <div className="mt-6 rounded-3xl border border-stone-200 bg-white p-7">
              <p className="text-sm text-stone-700 flex items-center gap-2"><FileText size={16} className="text-[#C2410C]" /> <b>{meta.papers_left}</b> of {meta.papers_per_pack} papers left in your pack</p>
              {meta.attempts?.length > 0 && (
                <div className="mt-4 space-y-1.5">
                  {meta.attempts.map((a) => (
                    <p key={a.test_id} className="text-xs text-stone-500">Attempt on {new Date(a.at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} — <b className={a.score >= 60 ? "text-emerald-600" : "text-amber-600"}>{a.score}/100</b></p>
                  ))}
                </div>
              )}
              <button onClick={start} disabled={busy || meta.papers_left <= 0} data-testid="prep-start"
                className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-stone-900 text-white font-medium hover:bg-stone-700 disabled:opacity-60">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Generate a fresh paper
              </button>
              {meta.papers_left <= 0 && <p className="mt-2 text-xs text-stone-500">Pack used up — great prep! The real assessment awaits.</p>}
            </div>
          )}
        </div>
      )}

      {stage === "live" && test && (
        <div className="max-w-3xl">
          <div className="rounded-2xl bg-stone-900 text-stone-50 px-5 py-3 flex items-center justify-between mb-6">
            <p className="text-sm font-medium">{test.context}</p>
            <p className="text-xs text-stone-400 inline-flex items-center gap-1"><Clock size={12} /> suggested {test.duration_min} min · untimed practice</p>
          </div>
          <div className="space-y-6">
            {test.sections.map((s) => (
              <div key={s.key}>
                <h2 className="font-display text-xl mb-3">{s.label}</h2>
                <div className="space-y-4">
                  {s.mcq.map((q) => (
                    <div key={q.index} className="rounded-2xl border border-stone-200 bg-white p-5">
                      <p className="text-sm font-medium whitespace-pre-wrap"><span className="text-stone-400 font-mono mr-2">Q{q.index + 1}</span>{q.q}</p>
                      <div className="mt-3 grid sm:grid-cols-2 gap-2">
                        {q.options.map((op, oi) => (
                          <label key={oi} className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border text-sm cursor-pointer ${answers[String(q.index)] === oi ? "border-stone-900 bg-stone-50 font-medium" : "border-stone-200 hover:border-stone-400"}`}>
                            <input type="radio" name={`q${q.index}`} checked={answers[String(q.index)] === oi}
                              onChange={() => setAnswers((a) => ({ ...a, [String(q.index)]: oi }))} className="accent-[#C2410C]" />
                            {op}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <button onClick={submit} disabled={busy} data-testid="prep-submit"
              className="w-full px-6 py-3.5 rounded-full bg-stone-900 text-white font-medium hover:bg-stone-700 disabled:opacity-60">
              {busy ? <Loader2 size={15} className="animate-spin inline" /> : "Check my answers"}
            </button>
          </div>
        </div>
      )}

      {stage === "result" && result && (
        <div className="max-w-3xl">
          <div className="rounded-3xl bg-stone-900 text-stone-50 p-7 mb-6">
            <span className="overline" style={{ color: "rgba(253,251,247,0.55)" }}>Practice result</span>
            <p className="font-display text-6xl mt-1">{result.score}<span className="text-2xl text-stone-400">/100</span></p>
            <div className="mt-3 flex flex-wrap gap-4">
              {result.sections.map((s) => (
                <p key={s.key} className="text-sm text-stone-300">{s.label}: <b className={s.percent >= 60 ? "text-emerald-400" : "text-amber-300"}>{s.percent}%</b></p>
              ))}
            </div>
          </div>
          <div className="space-y-3">
            {result.questions.map((r) => (
              <div key={r.index} className={`rounded-2xl border px-5 py-3.5 ${r.correct ? "border-emerald-200 bg-emerald-50/50" : "border-red-200 bg-red-50/50"}`}>
                <p className="text-sm font-medium flex items-start gap-2">
                  {r.correct ? <CheckCircle2 size={15} className="text-emerald-600 mt-0.5 shrink-0" /> : <XCircle size={15} className="text-red-500 mt-0.5 shrink-0" />}
                  <span className="whitespace-pre-wrap">{r.q}</span>
                </p>
                {!r.correct && <p className="text-xs text-stone-600 mt-1 ml-6">You: {r.your_answer || "—"} · Correct: <b>{r.correct_answer}</b></p>}
              </div>
            ))}
          </div>
          <button onClick={() => { setResult(null); setTest(null); load(); }}
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-stone-300 text-sm font-medium hover:border-stone-900">
            <RotateCcw size={15} /> Another paper
          </button>
        </div>
      )}
    </div>
  );
}
